import logo from 'assets/header-prevencor-xx.png';
import AssinaturaDigital from 'components/core/AssinaturaDigital';
import CustomFileInput from 'components/laudos/CustomFileInput';
import LaudoActionButtons from 'components/laudos/LaudoActionButtons';
import { useEffect, useState } from 'react';
import { Alert, Button, Card, Modal, Spinner } from 'react-bootstrap';
import { useLocation, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from 'services/api';
import { selectEcgById } from 'services/ecg';
import 'styles/laudo-base.css';
import useSWR from 'swr';
import { enviarArquivo, toWhatsappNumber } from 'utils/evolution';
import { baixarBlobPdf, blobToDataUrl, pdfMakeGetBase64, pdfMakeGetBlob } from 'utils/pdfBlob';
import { generateEcgPdf } from './generateEcgPdf';

export default function ReportEcg() {
  const location = useLocation();
  const { id } = useParams();
  const { data: ecgById, isLoading: loadingEcgById } = useSWR(
    id ? ['ecg', id] : null,
    () => selectEcgById(id),
    { revalidateOnFocus: false }
  );
  const ecg = location.state?.data || ecgById;

  const [showModalAnexo, setShowModalAnexo] = useState(false);
  const [fileToUpload, setFileToUpload] = useState([]);
  const [loadingUploadImage, setLoadingUploadImage] = useState(false);
  const [currentImageUrl, setCurrentImageUrl] = useState(null);
  const [currentImageBase64, setCurrentImageBase64] = useState(null);
  const [currentImageKey, setCurrentImageKey] = useState(null);
  const [loadingImage, setLoadingImage] = useState(false);

  useEffect(() => {
    const fetchExistingImage = async () => {
      if (!ecg?.CODECG) return;

      try {
        setLoadingImage(true);
        const { data } = await api.get('/arquivos/presign-view', {
          params: { codLaudo: ecg.CODECG, procedimento: 'ecg' },
        });

        setCurrentImageUrl(data.url);
        setCurrentImageKey(data.key);

        try {
          const response = await fetch(data.url);
          const blob = await response.blob();
          const base64 = await blobToDataUrl(blob);
          setCurrentImageBase64(base64);
        } catch {
          toast.warn('Erro na conversão da imagem!');
        }
      } catch {
        toast.warning('Imagem não encontrada!');
      } finally {
        setLoadingImage(false);
      }
    };

    fetchExistingImage();
  }, [ecg?.CODECG]);

  async function handleDeleteImage() {
    if (!currentImageKey) return;

    try {
      const res = await api.delete('/arquivos', {
        data: { key: currentImageKey },
      });
      toast.success(res.data.message);
      setCurrentImageUrl(null);
      setCurrentImageKey(null);
      setCurrentImageBase64(null);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Erro ao deletar imagem!');
    }
  }

  async function handleUploadFile(file) {
    if (!file.length) {
      toast.warn('Selecione um arquivo!');
      return false;
    }

    const fileExts = ['.jpg', '.jpeg', '.bmp', '.BMP'];
    const isInvalid = !fileExts.some((ext) => file[0].name.endsWith(ext));
    if (isInvalid) {
      toast.warn('Selecione apenas arquivos .jpg ou .bmp.');
      return false;
    }

    try {
      setLoadingUploadImage(true);

      const base64 = await blobToDataUrl(file[0]);
      setCurrentImageBase64(base64);

      const { data } = await api.post('/arquivos/presign-upload', {
        codLaudo: ecg?.CODECG,
        procedimento: 'ecg',
        filename: file[0].name,
        contentType: file[0].type,
      });

      await fetch(data.url, { method: 'PUT', body: file[0] });

      const viewResponse = await api.get('/arquivos/presign-view', {
        params: {
          codLaudo: ecg?.CODECG,
          procedimento: 'ecg',
          filename: data.key.split('/').pop(),
        },
      });

      setCurrentImageUrl(viewResponse.data.url);
      setCurrentImageKey(viewResponse.data.key);
      setShowModalAnexo(false);
      toast.success('Arquivo enviado com sucesso!');
      return true;
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Erro ao enviar arquivo!');
      return false;
    } finally {
      setLoadingUploadImage(false);
    }
  }

  async function handleImprimirEcg() {
    if (!ecg) return;

    try {
      const pdfDocGenerator = await generateEcgPdf({
        ecg,
        currentImageBase64,
      });
      const blob = await pdfMakeGetBlob(pdfDocGenerator);
      baixarBlobPdf({
        blob,
        fileName: `ECG-${ecg.CODECG}-${ecg.NOMEPAC}.pdf`,
      });
    } catch {
      toast.error('Erro ao gerar PDF! A imagem pode ser muito grande — tente reanexar em JPG.');
    }
  }

  async function handleEnviarEcg() {
    if (!ecg) return;

    const celularWhats = toWhatsappNumber(ecg.CELULARPAC);
    if (!celularWhats) {
      toast.warn('Número de celular não encontrado!');
      return;
    }

    try {
      const pdfDocGenerator = await generateEcgPdf({
        ecg,
        currentImageBase64,
      });

      const pdfBase64 = await pdfMakeGetBase64(pdfDocGenerator);
      const payload = {
        to: celularWhats,
        pdfBase64,
        paciente: {
          id: ecg?.CODPAC,
          nome: ecg?.NOMEPAC,
          medico: ecg?.REALIZOU,
        },
        codLaudo: ecg?.CODECG,
        fileName: `Laudo_ECG_${ecg?.CODECG}.pdf`,
        caption: `Olá *${ecg?.NOMEPAC}*, tudo bem? 😊\n\nSegue em anexo o seu *Laudo de Eletrocardiograma*.\n\nQualquer dúvida, nossa equipe estará à disposição no setor de atendimento: (14) 3402-2700`,
      };

      const result = await enviarArquivo(payload);
      if (result.success) {
        toast.success(result.message);
        return;
      }

      toast.error(result.message);
    } catch {
      toast.error(
        'Erro ao gerar PDF para envio! A imagem pode ser muito grande — tente reanexar em JPG.'
      );
    }
  }

  if (!ecg && loadingEcgById) {
    return (
      <Card className="mb-2 mt-2">
        <Card.Body>
          <div className="d-flex justify-content-center align-items-center py-5">
            <Spinner animation="border" variant="primary" />
          </div>
        </Card.Body>
      </Card>
    );
  }

  if (!ecg) {
    return (
      <Card className="mb-2 mt-2">
        <Card.Body className="text-center text-muted py-5">Laudo ECG não encontrado.</Card.Body>
      </Card>
    );
  }

  return (
    <>
      <Card className="mb-2 mt-2">
        <Card.Header className="text-center font-weight-bold">
          <img
            src={logo}
            alt="Prevencor"
            style={{ display: 'block', maxWidth: '100%', margin: '0 auto 8px' }}
            className="no-print"
          />
          LAUDO DE ELETROCARDIOGRAMA
        </Card.Header>
        <Card.Body>
          <strong className="mr-auto">Nome do Paciente: </strong>
          {`${ecg.CODPAC} - ${ecg.NOMEPAC}`}
          <br />
          <strong className="mr-auto">Data de Nasc: </strong>
          {`${ecg.dataNascimento}`}&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
          <strong className="mr-auto">Sexo: </strong>
          {ecg.SEXOPAC === 'M' ? 'Masculino' : 'Feminino'}
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
          <strong className="mr-auto">Idade: </strong>
          {`${ecg.idadePaciente} anos`}
          <br />
          <strong className="mr-auto">Convênio: </strong>
          {ecg.SIGLACONV}&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
          <strong className="mr-auto">Cod ECG: </strong>
          {ecg.CODECG}&nbsp;&nbsp;&nbsp;
          <strong className="mr-auto">Data do Exame: </strong>
          {ecg.dataEcg}
          <br />
          <strong className="mr-auto">Médico que Realizou: Dr. </strong>
          {ecg.REALIZOU}
          <br />
          <strong className="mr-auto">Médico que Solicitou: Dr. </strong>
          {ecg.SOLICITOU}
          <hr />
          {loadingImage ? (
            <div className="text-center p-3">
              <Spinner animation="border" variant="primary" />
              <p>Carregando imagem...</p>
            </div>
          ) : currentImageUrl ? (
            <div className="text-center p-2">
              <img
                src={currentImageUrl}
                alt="Eletrocardiograma"
                style={{ maxWidth: '100%', maxHeight: '100%' }}
                className="img-fluid"
              />
              <div className="mt-1 no-print">
                <Button variant="danger" size="sm" onClick={handleDeleteImage}>
                  Remover imagem
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-center p-3 text-muted">
              <p>Nenhuma imagem anexada</p>
            </div>
          )}
          <div>
            <strong>Descrição do Exame</strong>
            {(ecg.OBSERVACAO || '').split('\n').map((line, index) => (
              <p key={`ecg-observacao-${index}`} className="mb-1">
                {line}
              </p>
            ))}
          </div>
        </Card.Body>

        <Card.Footer className="text-center no-print">
          <LaudoActionButtons
            actions={[
              {
                key: 'timbrado',
                label: 'Timbrado',
                onClick: () => handleImprimirEcg(),
              },
              {
                key: 'anexar',
                label: 'Anexar',
                variant: 'warning',
                onClick: () => setShowModalAnexo(true),
              },
              {
                key: 'enviar',
                label: 'Enviar',
                variant: 'success',
                onClick: handleEnviarEcg,
              },
            ]}
          />
        </Card.Footer>

        <Card.Footer className="text-center">
          <AssinaturaDigital
            hash={ecg?.HASH_INTEGRIDADE}
            nome={ecg?.REALIZOU}
            showPrintButton={false}
          />
        </Card.Footer>
      </Card>

      <Modal
        show={showModalAnexo}
        centered
        onHide={() => {
          setShowModalAnexo(false);
          setFileToUpload([]);
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>Anexar arquivo</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <CustomFileInput
            id="file-upload"
            label="Selecionar arquivo"
            multiple={false}
            onChange={(e) => setFileToUpload(Array.from(e.target.files))}
          />
          <ul>
            {fileToUpload && fileToUpload.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <li style={{ marginRight: 5 }}>{fileToUpload[0].name}</li>
                <span
                  style={{ cursor: 'pointer', color: 'red' }}
                  onClick={() => setFileToUpload([])}
                >
                  X
                </span>
              </div>
            )}
          </ul>
          {currentImageUrl && (
            <Alert variant="warning">
              <Alert.Heading>Eletro já tem imagem!</Alert.Heading>
              <p>Delete a atual para anexar outra</p>
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          {loadingUploadImage ? (
            <Spinner animation="border" variant="dark" />
          ) : (
            <Button
              onClick={() => handleUploadFile(fileToUpload)}
              disabled={Boolean(currentImageUrl)}
            >
              Confirmar
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
}
